import { Link, useLocation } from "@tanstack/react-router";

import type { BlogLocale } from "@/features/blog/locales";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { isAlternateLocale } from "@/features/blog/locales";
import { PLATFORM_ALTERNATE_LOCALES } from "@/landing/lib/platform-i18n";

// Platform pages are the only landing pages with localized variants. The
// switcher sits in the footer, not the nav, so the nav stays the same width on
// every page.
const SWITCHER_LOCALES: readonly BlogLocale[] = [
  "en",
  ...PLATFORM_ALTERNATE_LOCALES,
];

/** The platform page's locale and slug, or null off platform pages. */
export function usePlatformLocale(): {
  locale: BlogLocale;
  slug: string;
} | null {
  const { pathname } = useLocation();
  const seg = pathname.split("/").filter(Boolean);
  const first = seg[0];
  if (first === "platform" && seg[1]) return { locale: "en", slug: seg[1] };
  if (first && isAlternateLocale(first) && seg[1] === "platform" && seg[2]) {
    return { locale: first, slug: seg[2] };
  }
  return null;
}

/** Language switcher for platform pages; renders nothing elsewhere. */
export function PlatformLanguageSwitcher() {
  const platform = usePlatformLocale();
  if (!platform) return null;
  const { locale, slug } = platform;
  return (
    <LanguageSwitcher
      currentLocale={locale}
      locales={SWITCHER_LOCALES}
      renderLink={(alt, children) =>
        alt === "en" ? (
          <Link to="/platform/$slug" params={{ slug }}>
            {children}
          </Link>
        ) : (
          <Link to="/$locale/platform/$slug" params={{ locale: alt, slug }}>
            {children}
          </Link>
        )
      }
    />
  );
}
